<script setup lang="ts">
import VAvatar from '@/modules/shared/components/v-avatar.vue'
import { VIcon } from '@/modules/shared/components'
import AccreditationBadge from '@/modules/screening/component/AccreditationBadge.vue'
import { AccreditationStatuses } from '@/modules/screening/types'
import type { InvestorCandidateExpanded } from '../types'

defineProps<{
  candidate: InvestorCandidateExpanded
  linked?: boolean
  linkTo?: object
}>()

function getInitials(name: string): string {
  if (!name) return '??'
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getCountryName(code: string): string {
  if (!code) return '—'
  try {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
    return regionNames.of(code) || code
  } catch {
    return code
  }
}
</script>

<template>
  <tr class="group transition-colors hover:bg-gray-50 dark:hover:bg-zinc-800/50">
    <!-- Name -->
    <td class="border-b border-gray-100 px-4 py-3 dark:border-zinc-800">
      <div class="flex items-center gap-3">
        <VAvatar
          :image="candidate.subject.profile_picture || null"
          :initials="getInitials(candidate.subject.name || '')"
          size="md"
        />
        <div class="flex items-center gap-2">
          <RouterLink
            v-if="linked && linkTo"
            :to="linkTo"
            class="text-sm font-semibold text-[#3b88af] underline decoration-[#3b88af]/30 underline-offset-2 hover:decoration-[#3b88af]"
          >
            {{ candidate.subject.name }}
          </RouterLink>
          <span v-else class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ candidate.subject.name }}
          </span>
          <a
            v-if="candidate.subject.linkedin_url"
            :href="candidate.subject.linkedin_url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center text-[#0077b5] opacity-70 transition-opacity hover:opacity-100"
          >
            <VIcon name="Linkedin" class="h-3.5 w-3.5" />
          </a>
          <span v-else class="flex items-center text-gray-400 opacity-20" title="No LinkedIn">
            <VIcon name="Linkedin" class="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </td>

    <!-- Email -->
    <td class="border-b border-gray-100 px-4 py-3 text-[13px] text-gray-500 dark:border-zinc-800 dark:text-zinc-400">
      {{ candidate.subject.email }}
    </td>

    <!-- Country -->
    <td class="border-b border-gray-100 px-4 py-3 text-[13px] text-gray-700 dark:border-zinc-800 dark:text-zinc-300">
      {{ getCountryName(candidate.subject.country) }}
    </td>

    <!-- Status -->
    <td class="border-b border-gray-100 px-4 py-3 dark:border-zinc-800">
      <slot name="status">
        <div class="flex flex-wrap items-center gap-2.5">
          <AccreditationBadge :status="candidate.subject.accredited_status || AccreditationStatuses.NotAccredited" />
        </div>
      </slot>
    </td>

    <!-- Actions -->
    <td class="border-b border-gray-100 px-4 py-3 dark:border-zinc-800">
      <slot name="actions" />
    </td>
  </tr>
</template>
